import { isEqual, map, size } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Checkbox, Grid, makeStyles, Tooltip, Typography } from '@material-ui/core'
import { ThemeProvider } from '@material-ui/styles'
import CallMergeIcon from '@material-ui/icons/CallMerge'
import InfoIcon from '@material-ui/icons/InfoOutlined'
import matchSorter from 'match-sorter'
import { useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import { ANCHOR_LEFT } from 'react-dates/constants'

import Loading from 'components/Loading'
import DateRangeFilter from 'components/filter/DateRangeFilter'
import { useDateRangeFilter } from 'filtering/hooks'
import { ContactCell, DateCell, PhoneNumberCell, ViewCell } from 'core/table/cells'
import Search from 'core/table/components/Search'
import PagedTable from 'core/table/components/PagedTable'
import DEFAULT_FILTERS from 'core/table/filters'
import { contactAccessor } from 'core/table/utils'
import { createContactSort } from 'components/table/sort'
import { useHasRole } from 'civic-champs-shared/auth/hooks'
import { INTERNAL_SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import useSelectColumn, { selectionColumnId } from 'core/table/table-hooks/useSelectColumn'
import { useMergePeople } from 'people/merge-people'
import { muiTheme } from 'legacy-theme'

const useStyles = makeStyles(theme => ({
  mergeInfo: {
    verticalAlign: 'middle',
    marginLeft: '5px',
    color: theme.palette.action.disabled,
  },
}))

const columns = [
  { id: 'id', Header: 'ID', accessor: 'id' },
  { id: 'firstName', Header: 'First Name', accessor: 'givenName' },
  { id: 'lastName', Header: 'Last Name', accessor: 'familyName' },
  {
    id: 'email',
    Header: 'Email',
    sortType: createContactSort('email'),
    accessor: contactAccessor({
      valuePath: 'email',
      verificationStatusPath: 'emailVerified',
      type: 'email',
    }),
    Cell: ContactCell,
  },
  {
    id: 'mobile',
    Header: 'Mobile',
    sortType: createContactSort('mobile'),
    accessor: contactAccessor({
      valuePath: 'phoneNumber',
      verificationStatusPath: 'phoneNumberVerified',
      type: 'phone',
    }),
    Cell: ContactCell,
  },
  {
    id: 'homePhoneNumber',
    Header: 'Home',
    accessor: 'homePhoneNumber',
    Cell: PhoneNumberCell,
  },
  {
    id: 'registration',
    Header: 'Login?',
    accessor: person => (person.cognitoSub ? 'Yes' : 'No'),
    disableSortBy: true,
  },
  {
    id: 'createdAt',
    Header: 'Added',
    accessor: 'createdAt',
    Cell: DateCell,
    filter: 'dateRange',
  },
  {
    Header: '',
    id: 'view',
    Cell: ViewCell(id => `/people/${id}`),
    disableSortBy: true,
  },
]

const personFullName = row => [row.original.givenName, row.original.familyName].filter(Boolean).join(' ')

const fuzzyMatchFilter = (rows, id, filterValue) =>
  matchSorter(rows, filterValue, {
    keys: [
      personFullName,
      'original.givenName',
      'original.familyName',
      'original.email',
      'original.phoneNumber',
      'original.homePhoneNumber',
      'original.id',
    ],
  })

fuzzyMatchFilter.autoRemove = value => !value

const filterTypes = {
  fuzzyMatch: fuzzyMatchFilter,
  ...DEFAULT_FILTERS,
}

const appendServerPeopleSelectColumn = (selectedByIdRef, togglePersonSelectionRef) => hooks => {
  hooks.visibleColumns.push(cols => [
    {
      id: selectionColumnId,
      Header: () => null,
      Cell: ({ row }) => (
        <ThemeProvider theme={muiTheme}>
          <Checkbox
            checked={selectedByIdRef.current.has(row.original.id)}
            onChange={() => togglePersonSelectionRef.current(row.original)}
          />
        </ThemeProvider>
      ),
    },
    ...cols,
  ])
}

const ClientPeopleTable = ({ people, tableRef, onSelectionChange }) => {
  const table = useTable(
    {
      data: people,
      columns,
      filterTypes,
      globalFilter: 'fuzzyMatch',
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useSelectColumn,
  )
  tableRef.current = table

  useEffect(() => {
    onSelectionChange(size(table.state.selectedRowIds))
  }, [table.state.selectedRowIds, onSelectionChange])

  return <PagedTable {...table} />
}

const ServerPeopleTable = ({
  people,
  pageCount,
  pageIndex,
  pageSize,
  serverSortBy,
  onSortChange,
  totalCount,
  onPageChange,
  onPageSizeChange,
  selectedByIdRef,
  togglePersonSelectionRef,
}) => {
  const table = useTable(
    {
      data: people,
      columns,
      manualPagination: true,
      manualSortBy: true,
      pageCount,
      autoResetPage: false,
      autoResetSortBy: false,
      getRowId: row => String(row.id),
      initialState: {
        pageIndex,
        pageSize,
        sortBy: serverSortBy,
      },
      state: {
        pageIndex,
        pageSize,
      },
    },
    useSortBy,
    usePagination,
    appendServerPeopleSelectColumn(selectedByIdRef, togglePersonSelectionRef),
  )
  const lastReportedSort = useRef(serverSortBy)

  useEffect(() => {
    if (isEqual(table.state.sortBy, serverSortBy)) {
      return
    }
    table.setSortBy(serverSortBy)
    lastReportedSort.current = serverSortBy
  }, [serverSortBy, table])

  useEffect(() => {
    if (!onSortChange || isEqual(table.state.sortBy, lastReportedSort.current)) {
      return
    }
    lastReportedSort.current = table.state.sortBy
    onSortChange(table.state.sortBy)
  }, [table.state.sortBy, onSortChange])

  return (
    <PagedTable {...table} totalCount={totalCount} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} />
  )
}

export const PeopleOverview = props => {
  const {
    loading,
    loadingProgress,
    people,
    title,
    refresh,
    serverSide,
    totalCount = 0,
    pageIndex = 0,
    onPageChange,
    pageSize = 25,
    onPageSizeChange,
    dateRange: serverDateRange,
    onDateRangeChange,
    sortBy: serverSortBy = [{ id: 'id', desc: false }],
    onSortChange,
    onSearchChange,
  } = props

  const [clientDateRange, setClientDateRange] = useDateRangeFilter()
  const [selectedById, setSelectedById] = useState(() => new Map())
  const selectedByIdRef = useRef(selectedById)
  selectedByIdRef.current = selectedById
  const togglePersonSelectionRef = useRef(() => {})

  const dateRange = serverSide ? serverDateRange : clientDateRange
  const setDateRange = serverSide ? onDateRangeChange : setClientDateRange

  const isInternalSuperAdmin = useHasRole(INTERNAL_SUPER_ADMIN)
  const mergePeople = useMergePeople()
  const classes = useStyles()

  const pageCount = serverSide ? (totalCount === 0 ? 0 : Math.ceil(totalCount / pageSize)) : undefined

  const togglePersonSelection = useCallback(person => {
    setSelectedById(prev => {
      const next = new Map(prev)
      if (next.has(person.id)) {
        next.delete(person.id)
      } else {
        next.set(person.id, person)
      }
      return next
    })
  }, [])
  togglePersonSelectionRef.current = togglePersonSelection

  const clientTableRef = useRef(null)
  const [clientSelectedCount, setClientSelectedCount] = useState(0)

  const handleQueryChange = query => {
    if (serverSide) {
      onSearchChange(query || '')
      return
    }
    clientTableRef.current.setGlobalFilter(query || undefined)
  }

  const handleDateRangeChange = newRange => {
    if (serverSide) {
      setDateRange(newRange)
      return
    }
    setDateRange(newRange)
    clientTableRef.current.setFilter('createdAt', newRange)
  }

  const selectedPeopleCount = serverSide ? selectedById.size : clientSelectedCount

  const handleMerge = async () => {
    if (selectedPeopleCount !== 2) return

    const peopleToMerge = serverSide
      ? Array.from(selectedById.values())
      : map(clientTableRef.current.selectedFlatRows, row => row.original)

    await mergePeople(peopleToMerge)
    if (serverSide) {
      setSelectedById(new Map())
    }
    refresh()
  }

  return (
    <div>
      <Grid container direction="column" spacing={3}>
        <Grid container xs={12} item justify="space-between" alignItems="center" direction="row">
          <Grid item xs={6}>
            <Typography variant="h4">{title}</Typography>
          </Grid>
          <Grid xs={6} container item alignItems="center" justify="flex-end" direction="row">
            <Search
              onQueryChange={handleQueryChange}
              debounce={serverSide ? 500 : 200}
              variant="outlined"
              placeholder="Search"
            />
            <Box style={{ padding: 4, border: 'solid 1px rgba(179, 179, 179)', borderRadius: 4, marginLeft: '10px' }}>
              <DateRangeFilter
                initialValues={dateRange}
                onFilterApplied={handleDateRangeChange}
                anchorDirection={ANCHOR_LEFT}
                noBorder={true}
              />
            </Box>
          </Grid>
        </Grid>
        {isInternalSuperAdmin && (
          <Grid item container spacing={1} alignItems="center" justify="flex-start">
            <Grid item>
              <Button variant="contained" color="secondary" disabled={selectedPeopleCount !== 2} onClick={handleMerge}>
                <CallMergeIcon style={{ marginRight: 5 }} />
                Merge
              </Button>
              {selectedPeopleCount > 2 && (
                <Tooltip title={`${selectedPeopleCount} selected.  Select only 2 for merge.`}>
                  <InfoIcon className={classes.mergeInfo} />
                </Tooltip>
              )}
            </Grid>
          </Grid>
        )}
        <Grid item>
          {loading ? (
            <Loading progress={loadingProgress} animateProgress={!serverSide && !!loadingProgress} />
          ) : serverSide ? (
            <ServerPeopleTable
              people={people}
              pageCount={pageCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              serverSortBy={serverSortBy}
              onSortChange={onSortChange}
              totalCount={totalCount}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              selectedByIdRef={selectedByIdRef}
              togglePersonSelectionRef={togglePersonSelectionRef}
            />
          ) : (
            <ClientPeopleTable people={people} tableRef={clientTableRef} onSelectionChange={setClientSelectedCount} />
          )}
        </Grid>
      </Grid>
    </div>
  )
}
